import ControlViewModel from '@/ViewModels/ControlViewModel';
import RADApplication from '@/Management/RADApplication';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import * as DownloadHelper from '@/Utility/DownloadHelper';
import EventHelper from '@/Utility/EventHelper';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import Utilities from '@/Utility/Utilities';
import MediumUploadHelper from '@/Utility/MediumUploadHelper';
import LocatorHelper from '@/Utility/LocatorHelper';

declare let moment: moment.MomentStatic;

export default class FileUploadViewModel extends ControlViewModel {

    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModel>('TextBoxViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    private MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModel>('MessageBoxViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    /**
     * Die Größe eines Chunks
     */
    private CHUNK_SIZE = 4 * 1024 * 1024;

    /**
     * Die maximale Größe einer Datei
     */
    private MAX_FILE_SIZE = MODEL.FileUploadLimitInMB * 1024 * 1024;

    /**
     * Die Breite des Controls
     */
    private CONTROL_WIDTH = 500;

    /**
     * Die vom Benutzer ausgewählte Datei
     */
    public file: KnockoutObservable<any>;

    /**
     * Die Breite des Vorschaubilds
     */
    public thumbWidth: string | number;

    /**
     * Die Höhe des Vorschaubilds
     */
    public thumbHeight: string | number;

    /**
     * Der Modus des Vorschaubilds
     */
    public previewMode: string;

    /**
     * Bestimmmt, ob überhaupt ein Vorschaubild angezeigt werden soll
     */
    public previewEnabled: boolean;

    /**
     * Bestimmmt, ob überhaupt ein Download möglich seien sollen
     */
    public downloadEnabled: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob zu kleine Vorschaubilder skaliert werden sollen
     */
    public scaleSmallThumb: boolean;

    /**
     * Definiert den Filter für auswählbare Dateien
     */
    public fileFilter: string;

    /**
     * Die PDO-Id des Objekts
     */
    public pdoId: KnockoutObservable<string>;

    /**
     * Die LockGroup-Id des Objekts
     */
    public lockGroupId: KnockoutObservable<string>;

    /**
     * Der Name des Attributs des Objekts
     */
    public attributeName: KnockoutObservable<string>;

    /**
     * Definiert einen Handler, der aufgerufen wird, sobald die Datei gewechselt wird
     */
    public onChange: Function;

    /**
     * Der Name der Datei
     */
    public name: KnockoutObservable<string>;

    /**
     * Der technische Name der Datei
     */
    public techName: KnockoutObservable<string>;

    /**
     * Die Größe der Datei
     */
    public size: KnockoutObservable<number>;

    /**
     * Gibt an ob die Drop-Funktion für das Control aktiv ist.
     */
    public isDropable: boolean;

    /**
     * Wird nach dem DropEvent ausgeführt.
     */
    public onDrop(droppedElements: CoreFrameInterfaces.DroppedElements): void {}

    /**
     * Prüft ob die gedraggten Elemente zulässig sind
     * @param draggedElements
     */
    public checkDraggedElementsAllowed(draggedElements: CoreFrameInterfaces.DraggedElements): boolean {
        return true;
    }

    public additionalInfo: KnockoutObservable<string>;

    public loadOperation: KnockoutObservable<any>;

    /**
     * Die angezeigte TextBox
     */
    public textBox: TextBoxViewModel;

    /**
     * Das Bild zur Darstellung des Dateiformats
     */
    public image: ImageViewModel;

    /**
     * Der Link zum Auswählen einer Datei
     */
    public linkSelect: LinkViewModel;

    /**
     * Die URL zum Herunterladen der Datei
     */
    public downloadUrl: KnockoutComputed<string>;

    private previewObjectUrl : string | null = null;
    
    /**
     * Die URL für die Dateivorschau
     */
    public getPreviewUrl(): string {
        return '';
    }

    /**
     * Der Link zum Herunterladen einer Datei
     */
    public linkDownload: LinkViewModel;

    /**
     * Der Link zum Entfernen der ausgewählten Datei
     */
    public linkRemove: LinkViewModel;

    /**
     * Der Link zum Ansehen einer Datei
     */
    public linkView: LinkViewModel;

    /**
     * Das Bild der Live-Vorschau
     */
    public preview: ImageViewModel;

    /**
     * Der eindeutige Locator des input-Elements
     */
    public fileUploadFormLocator: KnockoutComputed<string>;

    /**
     * Setzt die neue Datei und fügt einen TechName hinzu.
     */
    public setFile(file: File): void {}

    /**
     * Das ViewModel für ein FileUpload
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params: any) {
        super(params);
        this.calculateLocator();
        this.file = this.observableOrDefault(params.file, null);
        this.thumbWidth = this.paramOrDefault(params.thumbWidth, -1, this.parseThumbDimension);
        this.thumbHeight = this.paramOrDefault(params.thumbHeight, -1, this.parseThumbDimension);

        this.previewMode = this.paramOrDefault(params.previewMode, 'Popup', (value: any) => {
            return ['Popup', 'FixedImage', 'None'].indexOf(value) > -1 ? value : 'Popup';
        });

        this.previewEnabled = this.paramOrDefault(params.previewEnabled, true);
        this.downloadEnabled = this.observableOrDefault(params.downloadEnabled, true);
        this.scaleSmallThumb = this.paramOrDefault(params.scaleSmallThumb, false);

        this.fileFilter = this.paramOrDefault(params.fileFilter, null, (value: any) => {
            let newValue = null;
            if (value && Utilities.isString(value)) {
                newValue = value.replace('*', '');
                newValue = newValue.replace(/\s+/g, '');
                newValue = newValue.toLowerCase();
            }

            return newValue;
        });

        this.pdoId = this.observableOrDefault(params.pdoId, null);
        this.lockGroupId = this.observableOrDefault(params.lockGroupId, null);
        this.attributeName = this.observableOrDefault(params.attributeName, '');
        this.onChange = this.handlerOrDefault(params.onChange);
        this.name = this.observableOrDefault(params.name, '');
        this.techName = this.observableOrDefault(params.techName, '');
        this.size = this.observableOrDefault(params.size, 0);
        this.isDropable = this.paramOrDefault(params.isDropable, false);

        this.onDrop = (droppedElements: any) => {
            if (droppedElements.files.length === 1) {
                this.setFile(droppedElements.files[0]);
            }
        }

        this.checkDraggedElementsAllowed = (draggedElements: any) => {
            return EventHelper.validateDraggedElements(draggedElements, true, false, 1);
        }

        this.additionalInfo = this.observableOrDefault(params.additionalInfo, '');
        this.loadOperation = this.observableOrDefault(params.loadOperation, null);

        this.defineSubscription(this.file, file => {
            if (!this.isEnabled())
                return;

            if (file != null) {
                // Beim initialen Aufruf ist der techName nicht gesetzt und dieser Teil wird nicht ausgeführt.
                if (file.techName) {
                    if (file.size === 0) {
                        this.removeFile();
                        RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createErrorMessageBox(null, 'FILE_SIZE_ZERO'));
                    } else if (file.size < this.MAX_FILE_SIZE) {
                        this.increaseBusyCounter();
                        this.sendFile(file, 0, () => {
                            this.name(file.name);
                            this.size(file.size);
                            this.techName(file.techName);

                            this.onChange(file);

                            this.setPreviewSource(this.getPreviewUrl());
                        }).finally(() => {
                            this.decreaseBusyCounter();
                        });
                    } else {
                        this.removeFile();

                        let content : string = ResourceManager.getInstance().get('FILE_SIZE_EXCEED_PARAM');
                        content = content.replace('$$size$$', "" + MODEL.FileUploadLimitInMB)
                        RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createErrorMessageBoxWithoutErgnames(content));
                    }
                }
            } else {
                this.name('');
                this.size(0);
                this.techName('');
            }
        });

        this.textBox = this.createTextBoxViewModel();
        this.image = this.createImageViewModel();
        this.linkSelect = this.createLinkSelectViewModel();

        this.downloadUrl = this.defineComputed(() => {
            let mediumHandler = RADApplication.getInstance().radEnvironmentMediumHandler;
            let fileName = this.name();
            let technicalFileName = this.techName();
            let pdoId = this.pdoId();
            let lockGroupId = this.lockGroupId();
            let attributeName = this.attributeName();
            let query;

            if (pdoId != null) {
                query = '?PDOID=' + pdoId + '&LockGroupID=' + lockGroupId + '&AttributeName=' + attributeName + '&Preview=false';
            } else {
                query = '?Filename=' + fileName + '&TechnicalFilename=' + technicalFileName + '&Preview=false';
            }

            return mediumHandler + query;
        });

        this.getPreviewUrl = () => {
            let mediumHandler = RADApplication.getInstance().radEnvironmentMediumHandler;
            let fileName = this.name();
            let technicalFileName = this.techName();
            let pdoId = this.pdoId();
            let lockGroupId = this.lockGroupId();
            let attributeName = this.attributeName();
            let preview = this.previewMode !== 'None';
            let thumbWidth = typeof this.thumbWidth === 'number' && this.thumbWidth > this.CONTROL_WIDTH || this.thumbWidth === 0 ? this.CONTROL_WIDTH : this.thumbWidth;
            let thumbHeight = typeof this.thumbHeight === 'number' && this.thumbHeight > this.CONTROL_WIDTH || this.thumbHeight === 0 ? this.CONTROL_WIDTH : this.thumbHeight;
            let query;

            if (!fileName) {
                return null;
            }

            if (pdoId != null) {
                query = '?PDOID=' + pdoId + '&LockGroupID=' + lockGroupId + '&AttributeName=' + attributeName;
            } else {
                query = '?Filename=' + fileName + '&TechnicalFilename=' + technicalFileName;
            }

            query += '&Preview=' + preview;
            query += '&ThumbWidth=' + thumbWidth;
            query += '&ThumbHeight=' + thumbHeight;
            query += '&ScaleThumb=' + this.scaleSmallThumb;

            return mediumHandler + query;
        }

        this.linkDownload = this.createDownloadLinkViewModel();
        this.linkRemove = this.createRemoveLinkViewModel();
        this.linkView = this.createViewLinkViewModel();
        this.preview = this.createPreviewImageViewModel();
        
        this.setPreviewSource(this.getPreviewUrl(), 0);

        this.setFile = (file: any) => {
            file.techName = moment().format('x');
            this.file(file);
        }

        (() => {
            let name = this.name();
            let size = this.size();

            if (name) {
                this.file({ name: name, size: size });
            }

            this.callLoadOperation(null);
        })();

        this.defineSubscription(this.name, (name: any) => {
            if (String.isNullOrEmpty(name)) {
                this.file(null);
            } else {
                let file = this.file();
                if (file != null) {
                    if (!(file instanceof File)) {
                        file.name = name;
                    }
                } else {
                    this.file({
                        name: name,
                        size: this.size()
                    });
                }
            }
        });

        this.defineSubscription(this.size, (size: any) => {
            let file = this.file();
            if (file != null && !(file instanceof File)) {
                file.size = size;
            }
        });
    }

    private parseThumbDimension(val: any, def: any): number {
        let ret = parseInt(val);
        if (isNaN(ret)) {
            return def;
        }

        return ret;
    }

    private openDownloadDialog(): void {
        let downloadUrl = this.downloadUrl();
        DownloadHelper.openDownloadDialogFromApplicationAsync(downloadUrl);
    }

    private calculateLocator(): void {
        if (Utilities.isNullOrUndefined(this.locator)) {
            this.locator = this.defineComputed(() => {
                // use a fallback-locator
                return `inputFile`;
            });
        }

        this.fileUploadFormLocator = LocatorHelper.buildLocator(this.locator, 'inputFile');
    }

    private callLoadOperation(successCallback: any) {
        let loadOperation = this.loadOperation();
        let additionalInfo = this.additionalInfo();

        if (!loadOperation) {
            return;
        }

        let currentDetailViewContext = RADApplication.getInstance().currentDetailViewContext();

        if (!currentDetailViewContext || !this.downloadEnabled()) {
            return;
        }

        currentDetailViewContext.callOperationUnchecked(loadOperation, ['']).then((result: any) => {
            if (this.validateStatus(result)) {
                this.techName(result.returnValue);
                if (!result.returnValue) {
                    this.name('');
                }

                // RefreshUrlAndPreview();

                if (successCallback && Utilities.isFunction(successCallback)) {
                    successCallback(result);
                }
            }
        });
    }

    private getExtension(name: string): string {
        if (name) {
            return name.split('.').pop();
        }

        return '';
    }

    private setPreviewSource(source: any, delay : number = 1000) {
        this.async(() => {
            if (!source) {
                this.updatePreviewSource(null);
            } else {
                this.fetchPreviewImage(source).then(([previewContent, _]) => {
                    this.updatePreviewSource(previewContent);
                })
            }
        }, delay);
    }
    
    private updatePreviewSource(source: string | null) {
        if (this.preview != null && this.preview.source != null) {
            // Selbst wenn sich die URL nicht geändert, muss das Bild neu aufgerufen
            // werden. Durch das notifySubscribers wird ein Reload im ImageViewModel ausgelöst
            if (source == this.preview.source()) {
                this.preview.source.notifySubscribers(source);
            } else {
                this.preview.source(source);
            }
        }
    }

    /**
     * Entfernt die ausgewählte Datei
     */
    private removeFile(): void {
        if (!this.isEnabled())
            return;

        this.file(null);
        this.onChange(null);
        this.setPreviewSource(null);
    }

    /**
     * Sendet die ausgewählte Datei an den Server
     */
    private sendFile(file: any, startByte: number, onSuccess: () => void): Promise<boolean> {
        const fileName = file.techName;
        const extension = this.getExtension(file.name);
        const fileSize = file.size;

        const mediumHandler = RADApplication.getInstance().radEnvironmentMediumHandler;
        const complete = startByte + this.CHUNK_SIZE >= fileSize;

        return new Promise(resolve => {
            this.createChunk(file, startByte, (data: string | ArrayBuffer, bytesLoaded: number) => {
                const url = `${mediumHandler}?Filename=${fileName}&Extension=${extension}&FullFileSize=${fileSize}&StartByte=${startByte}&Complete=${complete}`
                fetch(url, {
                    method: 'POST',
                    body: data,
                    headers: {
                        token: RADApplication.getInstance().token,
                    }
                }).then(async (response: Response) => {
                    if (!response.ok) {
                        this.onSendFileFailed(response.status)
                        resolve(false);
                        return;
                    }

                    const data = await response.text();
                    if (data === 'OKAY') {
                        if (bytesLoaded < fileSize) {
                            const success = await this.sendFile(file, bytesLoaded, onSuccess);
                            resolve(success);
                        } else {
                            onSuccess();
                            resolve(true);
                        }
                    } else {
                        this.onSendFileFailed(null);
                        resolve(false);
                    }
                })
            });
        });
    }

    /**
    * Wenn der Datei Upload fehlgeschlagen ist
    */
    private onSendFileFailed(httpStatus?: number): void {
        this.removeFile();
        MediumUploadHelper.showFileUploadError(httpStatus);
    }

    /**
     * Erstellt einen Chunk aus der ausgewählten Datei und übergibt dessen Daten an einen Callback
     */
    private createChunk(file: any, startByte: number, callback: (result: string | ArrayBuffer, stopByte: number) => void) {
        const fileSize = file.size;
        const stopByte = Math.min(fileSize, startByte + this.CHUNK_SIZE);
        const reader = new FileReader();
        const blob = file.slice(startByte, stopByte);

        reader.onloadend = (event: ProgressEvent<FileReader>) => {
            if (event.target.readyState == FileReader.DONE) {
                callback(event.target.result, stopByte);
            }
        };

        reader.readAsArrayBuffer(blob);
    }

    // ViewModel Erstellung
    private createTextBoxViewModel(): TextBoxViewModel {
        return new this.TextBoxViewModel({
            isEnabled: false,
            value: this.name,
            tooltip: this.tooltip,
            isValid: this.isValid,
            invalidationText: this.invalidationText
        });
    }

    private createImageViewModel(): ImageViewModel {
        return new this.ImageViewModel({
            isFileType: this.defineComputed(() => {
                let name = this.name();

                if (name) {
                    return true;
                } else {
                    return false;
                }
            }),
            source: this.defineComputed(() => {
                let source = 'blank.png';
                let name = this.name();

                if (name) {
                    let extension = name.split('.').pop();
                    source = extension + '.png';
                }

                return source;
            }),
            alternativeText: new this.LabelViewModel({
                caption: this.defineComputed(() => {
                    let source = 'blank.png';
                    let name = this.name();

                    if (name) {
                        let extension = name.split('.').pop();

                        return extension;
                    }

                    return '';
                })
            })
        });
    }

    private createLinkSelectViewModel(): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'chooseFile',
            isFocusable: this.isFocusable,
            isEnabled: this.defineComputed(() => {
                return this.file() == null;
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.MEDIUM_SELECT,
                parentLocator: this.locator,
                locator: 'chooseFile.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MEDIUM_UPLOAD_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MEDIUM_UPLOAD_TOOLTIP'
            }),
            isToolBarLink: true,
            isVisible: this.isEnabled
        });
    }

    private createDownloadLinkViewModel(): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'download',
            isFocusable: this.isFocusable,
            isEnabled: this.defineComputed(() => {
                return this.file() != null && this.downloadEnabled();
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.MEDIUM_DOWNLOAD,
                parentLocator: this.locator,
                locator: 'download.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MEDIUM_DOWNLOAD_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MEDIUM_DOWNLOAD_TOOLTIP'
            }),
            onClick: this.openDownloadDialog.bind(this),
            isToolBarLink: true
        });
    }

    private createRemoveLinkViewModel(): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'remove',
            isFocusable: this.isFocusable,
            isEnabled: this.defineComputed(() => {
                return this.file() != null;
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.MEDIUM_DELETE,
                parentLocator: this.locator,
                locator: 'remove.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MEDIUM_DELETE_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MEDIUM_DELETE_TOOLTIP'
            }),
            onClick: this.removeFile.bind(this),
            isToolBarLink: true,
            isVisible: this.isEnabled
        });
    }

    private createViewLinkViewModel(): LinkViewModel {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'viewFile',
            isFocusable: this.isFocusable,
            isEnabled: this.defineComputed(() => {
                if (this.file() == null) {
                    return false;
                }

                let fileExtension = this.file().name.split('.').pop();
                let previewExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];

                return previewExtensions.indexOf(fileExtension.toLowerCase()) > -1;
            }),
            isVisible: this.defineComputed(() => {
                if (this.previewMode !== 'Popup') {
                    return false;
                }

                return !this.isSummaryViewMode;
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MEDIUM_PREVIEW_TOOLTIP'
            }),
            image: new this.ImageViewModel({
                source: GlobalConfiguration.inlineImages.MEDIUM_VIEWFILE,
                parentLocator: this.locator,
                locator: 'viewFile.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MEDIUM_PREVIEW_TOOLTIP'
                })
            }),
            isToolBarLink: true
        });
    }
    
    private setPreviewObjectUrl(url: string | null) {
        if (this.previewObjectUrl != null) {
            window.URL.revokeObjectURL(this.previewObjectUrl);
        }
        
        this.previewObjectUrl = url;
    }
    
    private async fetchPreviewImage(url: string | null): Promise<[string, string] | null> {
        const [blob, filename] = await DownloadHelper.fetchBlobFromApplicationAsync(url);
        this.setPreviewObjectUrl(window.URL.createObjectURL(blob));
        return [this.previewObjectUrl, filename]
    }
    
    private createPreviewImageViewModel(): ImageViewModel {
        return new this.ImageViewModel({
            isBinary: true,
            noCache: true,
            source: null,
            isFocusable: this.isFocusable,
            isVisible: this.defineComputed(() => {
                if (this.file() == null) {
                    return false;
                }

                if (this.previewMode !== 'FixedImage') {
                    return false;
                }

                let fileExtension = this.file().name.split('.').pop();
                let previewExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'];

                return previewExtensions.indexOf(fileExtension.toLowerCase()) > -1 && !this.isSummaryViewMode;
            }),
            alternativeText: new this.LabelViewModel({
                ergName: 'MEDIUM_PREVIEW'
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MEDIUM_PREVIEW'
            })
        });
    }
    
    dispose() {
        this.setPreviewObjectUrl(null);
        super.dispose();
    }
}

ComponentManager.setViewModel('FileUploadViewModel', FileUploadViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');